import { useState } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Typography,
	Skeleton,
	ToggleButtonGroup,
	ToggleButton,
	Tooltip,
} from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import QuadPanel from 'features/IRWorkstation/components/Common/QuadPanel';
import SectionHeader from 'features/IRWorkstation/components/Tasks/SectionHeader';
import SalesforceTaskRow from 'features/IRWorkstation/components/Tasks/SalesforceTaskRow';
import {
	groupTasksByPriority,
	groupTasksByDueDate,
	filterTasksCreatedToday,
} from 'features/IRWorkstation/utils/sortWorkQueue';

const WorkQueueRowSkeleton = () => (
	<Box
		sx={{
			display: 'flex',
			alignItems: 'flex-start',
			gap: '9px',
			padding: '9px 15px',
			borderBottom: '0.5px solid',
			borderColor: 'border.light',
		}}
	>
		<Skeleton
			variant='circular'
			width={7}
			height={7}
			sx={{ mt: '4px', flexShrink: 0 }}
		/>
		<Box sx={{ flex: 1 }}>
			<Skeleton width='55%' height={18} />

			<Box
				sx={{
					display: 'flex',
					gap: '6px',
					mt: '2px',
				}}
			>
				<Skeleton width={60} height={14} />
				<Skeleton width={50} height={14} />
				<Skeleton width={90} height={14} />
			</Box>
		</Box>
	</Box>
);

const WorkQueueSkeleton = () => (
	<Box data-testid='work-queue-skeleton'>
		{['Overdue', 'Due this week', 'Upcoming'].map((section) => (
			<Box key={section}>
				<Box sx={{ px: 2, py: 1 }}>
					<Skeleton width={90} height={18} />
				</Box>

				<WorkQueueRowSkeleton />
				<WorkQueueRowSkeleton />
			</Box>
		))}
	</Box>
);

const WorkQueueWidget = ({
	onOpenTaskDrawer,
	salesforceTasks,
	completedTodayTasks = [],
	loading,
	error,
}) => {
	const [sortBy, setSortBy] = useState('priority');

	// Call helper methods to group and sort tasks based on sort model
	const workQueueSections = (() => {
		let sections;

		switch (sortBy) {
			case 'priority':
				sections = groupTasksByPriority(salesforceTasks);
				break;

			case 'date':
				sections = groupTasksByDueDate(salesforceTasks);
				break;

			case 'whatsNew':
				sections = filterTasksCreatedToday(salesforceTasks);
				break;

			default:
				sections = groupTasksByPriority(salesforceTasks);
		}

		// Add the Completed Today section at the bottom
		if (completedTodayTasks?.length) {
			sections.push({
				section: 'Completed Today',
				items: completedTodayTasks,
			});
		}

		return sections;
	})();

	const handleSortChange = (_e, newSortBy) => {
		if (newSortBy !== null) {
			setSortBy(newSortBy);
		}
	};

	// Used to determine whether to show an indicator that there are new tasks to review
	// Access first section (What's new) - if there're completed tasks there will be a second section
	const hasTasksCreatedToday =
		!!filterTasksCreatedToday(salesforceTasks)[0]?.items?.length;

	const SortControls = () => (
		<ToggleButtonGroup
			value={sortBy}
			exclusive
			onChange={handleSortChange}
			size='small'
			sx={{
				ml: 2,
				height: 24,
				borderRadius: 2,
				'& .MuiToggleButton-root': {
					px: 3,
					textTransform: 'none',
					borderColor: 'divider',
					backgroundColor: 'background.subtle',
				},
				'& .MuiToggleButton-root.Mui-selected': {
					bgcolor: 'primary.main',
					color: 'primary.contrastText',
					'&:hover': {
						bgcolor: 'primary.dark',
					},
				},
			}}
		>
			<ToggleButton value='priority'>
				<Tooltip title='Groups items by priority: High, Medium, and Low'>
					<span>Priority</span>
				</Tooltip>
			</ToggleButton>

			<ToggleButton value='date'>
				<Tooltip title='Groups items by date deadline. Overdue, Due this week, and Upcoming'>
					<span>Date</span>
				</Tooltip>
			</ToggleButton>

			<ToggleButton value='whatsNew'>
				<Tooltip title='Shows only items created today, ordered by priority'>
					<span>New</span>
					{hasTasksCreatedToday ? (
						<CircleIcon
							aria-label='New tasks created today'
							sx={{ ml: 0.5, color: 'error.main', fontSize: '8px', mb: 0.75 }}
						/>
					) : null}
				</Tooltip>
			</ToggleButton>
		</ToggleButtonGroup>
	);

	return (
		<QuadPanel
			title='Work Queue'
			widgetControls={<SortControls />}
			badge={loading ? 'Loading...' : `${salesforceTasks.length} open`}
			badgeColor={
				loading ? 'gray' : salesforceTasks.length > 0 ? 'red' : 'gray'
			}
			subtitle={
				error === 'not_connected' || error === 'reconnect_required'
					? 'Connect your CRM to see live tasks'
					: 'Live from your CRM'
			}
		>
			{/* Show a skeleton layout while CRM tasks are loading */}
			{loading ? (
				<WorkQueueSkeleton />
			) : error ? (
				<Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
					<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
						{error === 'not_connected'
							? 'Connect your CRM to view tasks here.'
							: error === 'reconnect_required'
								? "Connection expired. Please remove and reconnect your CRM from Launchpad's Connections page."
								: 'Unable to load tasks. Please try again later.'}
					</Typography>
				</Box>
			) : salesforceTasks.length === 0 ? (
				<Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
					<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
						No open tasks
					</Typography>
				</Box>
			) : (
				workQueueSections.map((workQueueSection) => (
					<Box key={workQueueSection.section}>
						<SectionHeader label={workQueueSection.section} />
						{workQueueSection.items.map((task) => (
							<SalesforceTaskRow
								key={task.id}
								task={task}
								onClick={onOpenTaskDrawer}
							/>
						))}
					</Box>
				))
			)}
		</QuadPanel>
	);
};

WorkQueueWidget.propTypes = {
	onOpenTaskDrawer: PropTypes.func.isRequired,
	salesforceTasks: PropTypes.array.isRequired,
	completedTodayTasks: PropTypes.array,
	loading: PropTypes.bool.isRequired,
	error: PropTypes.string,
};

export default WorkQueueWidget;
